import {IApiEndpoint, IOperationResponse, IPagination} from "@localization/siteMap/siteMapInterfaces.ts";
import {IUsuarioFindDto} from "@models/conta/contaDto.ts";
import {siteMapApi} from "@localization/siteMap/siteMap.ts";
import {IUsuario} from "@models/conta/IUsuario.ts";
import {IRoleFindDto, IRoleMembroFindDto, IRoleNovaDto} from "@models/roles/rolesDto.ts";
import {IRole, IRoleMembro} from "@models/roles/roles.ts";
import type {IAssociacaoPermissaoRoleDto} from "@models/seguranca/associaPermissaoRoleDto.js";
import type {IPermissaoRole} from "@models/seguranca/permissaoRole.js";

export const _SegurancaEndpoints = {
    disableConta: (filter: IUsuarioFindDto, acaoConfirmada: boolean): IApiEndpoint => ({
        url: '/api/Conta/DisableConta',
        method: 'DELETE',
        query: {...filter, acaoConfirmada},
    }),
    enableConta: (filter: IUsuarioFindDto, acaoConfirmada: boolean): IApiEndpoint => ({
        url: '/api/Conta/EnableConta',
        method: 'PATCH',
        query: {...filter, acaoConfirmada},
    }),
    getRoles: (data: IRoleFindDto, pagination: IPagination): IApiEndpoint => ({
        url: '/api/Conta/GetRoles',
        pagination: pagination,
        query: data,
        method: 'GET'
    }),
    createRole: (data: IRoleNovaDto): IApiEndpoint => ({
        url: '/api/Conta/CreateRole',
        body: data,
        method: 'POST'
    }),
    addMembroRole: (data: { idRole: number, idUsuario: number }[]): IApiEndpoint => ({
        url: '/api/Conta/AddMembroRole',
        body: data,
        method: 'POST'
    }),
    removeMembroRole: (data: { idRole: number, idUsuario: number }[]): IApiEndpoint => ({
        url: '/api/Conta/RemoveMembroRole',
        body: data,
        method: 'DELETE'
    }),
    getMembrosRoles: (filter: IRoleMembroFindDto, pagination: IPagination): IApiEndpoint => ({
        url: '/api/Conta/GetRolesMembro',
        query: filter,
        pagination: pagination,
        method: 'GET'
    }),
    enableRole: (filter: IRoleFindDto, acaoConfirmada: boolean): IApiEndpoint => ({
        url: '/api/Conta/EnableRole',
        query: {...filter, acaoConfirmada},
        method: 'PATCH'
    }),
    disableRole: (filter: IRoleFindDto, acaoConfirmada: boolean): IApiEndpoint => ({
        url: '/api/Conta/DisableRole',
        query: {...filter, acaoConfirmada},
        method: 'DELETE'
    }),
    isAutorizado: (): IApiEndpoint => ({
        url: '/api/Conta/Authorized',
        method: 'GET'
    }),
    associaPermissaoRole: (body: IAssociacaoPermissaoRoleDto): IApiEndpoint => ({
        url: '/api/Seguranca/AssociarRole',
        body: body,
        method: 'PUT',
    }),
    getPermissoesPesquisa: (search: string, pagination: IPagination): IApiEndpoint => ({
        url: '/api/Seguranca/GetPermissoesPesquisa',
        pagination: pagination,
        query: {search},
        method: 'GET'
    }),
    getMinhasPermissoes: (pagination: IPagination): IApiEndpoint => ({
        url: '/api/Seguranca/GetMinhasPermissoes',
        pagination: pagination,
        method: 'GET'
    }),
    GetContas: (filter: IUsuarioFindDto, pagination: IPagination): IApiEndpoint<IUsuario[]> => ({
        url: '/api/Conta/GetContas',
        pagination: pagination,
        query: filter,
        method: 'GET'
    })
}

export const disableConta = async (filter: IUsuarioFindDto, acaoConfirmada: boolean): Promise<IOperationResponse<IUsuario[]>> => {
    const endpoint = _SegurancaEndpoints.disableConta(filter, acaoConfirmada);

    return await siteMapApi.fetch<IOperationResponse>(endpoint)
}

export const enableConta = async (filter: IUsuarioFindDto, acaoConfirmada: boolean): Promise<IOperationResponse<IUsuario[]>> => {
    const endpoint = _SegurancaEndpoints.enableConta(filter, acaoConfirmada);

    return await siteMapApi.fetch<IOperationResponse>(endpoint)
}

export const getRoles = async (data: IRoleFindDto, pagination: IPagination): Promise<IOperationResponse<IRole[]>> => {
    const endpoint = _SegurancaEndpoints.getRoles(data, pagination);

    return await siteMapApi.fetch<IOperationResponse<IRole[]>>(endpoint)
}

export const createRole = async (data: IRoleNovaDto): Promise<IOperationResponse<IRole>> => {
    const endpoint = _SegurancaEndpoints.createRole(data);

    return await siteMapApi.fetch<IOperationResponse<IRole>>(endpoint)
}

export const addMembroRole = async (data: {
    idRole: number,
    idUsuario: number
}[]): Promise<IOperationResponse<IRoleMembro[]>> => {
    const endpoint = _SegurancaEndpoints.addMembroRole(data);

    return await siteMapApi.fetch<IOperationResponse>(endpoint)
}

export const removeMembroRole = async (data: {
    idRole: number,
    idUsuario: number
}[]): Promise<IOperationResponse<IRoleMembro[]>> => {
    const endpoint = _SegurancaEndpoints.removeMembroRole(data);

    return await siteMapApi.fetch<IOperationResponse>(endpoint)
}


export const getRolesMembro = async (filter: IRoleMembroFindDto, pagination: IPagination): Promise<IOperationResponse<IRoleMembro[]>> => {
    const endpoint = _SegurancaEndpoints.getMembrosRoles(filter, pagination);

    return await siteMapApi.fetch<IOperationResponse<IRoleMembro[]>>(endpoint)
}

export const enableRole = async (filter: IRoleFindDto, acaoConfirmada: boolean): Promise<IOperationResponse<IRole[]>> => {
    const endpoint = _SegurancaEndpoints.enableRole(filter, acaoConfirmada);

    return await siteMapApi.fetch<IOperationResponse>(endpoint)
}

export const disableRole = async (filter: IRoleFindDto, acaoConfirmada: boolean): Promise<IOperationResponse<IRole[]>> => {
    const endpoint = _SegurancaEndpoints.disableRole(filter, acaoConfirmada);

    return await siteMapApi.fetch<IOperationResponse>(endpoint)
}

export const AssociaPermissaoRole = async (body: IAssociacaoPermissaoRoleDto) => {
    const endpoint = _SegurancaEndpoints.associaPermissaoRole(body);

    return await siteMapApi.request<IPermissaoRole[]>(endpoint)
}
